// Retryable POST helper for chat / "must succeed" endpoints (web).
//
// Mirrors mobile/api/client.ts:postWithRetry so both clients show the
// same retry behaviour and surface the same friendly error kinds.
//
// Schedule (default, for short REST endpoints — 3 attempts max):
//   attempt 1 → 10s timeout
//   wait 800ms
//   attempt 2 → 15s timeout
//   wait 2000ms
//   attempt 3 → 15s timeout
//
// Schedule (`/chat` and other LLM/grounded-search endpoints — see
// `PER_PATH_TIMEOUTS` below): SINGLE attempt with a generous 75s
// timeout. Retrying a 30-second Gemini-grounded request triples cost
// for no UX gain (each retry kicks off a brand-new grounded search).
//
// Caller surfaces a friendly error only AFTER all attempts have failed.

import axios from "axios";

export class ChatRequestError extends Error {
    constructor(kind, message, httpStatus = null) {
        super(message);
        this.name = "ChatRequestError";
        this.kind = kind;
        this.httpStatus = httpStatus;
    }
}

const DEFAULT_TIMEOUTS_MS = [10_000, 15_000, 15_000];
const RETRY_BACKOFFS_MS = [800, 2_000];

// Endpoints whose handler does meaningful LLM / grounded-search work and
// therefore needs a single generous attempt instead of the short-retry
// ladder. Retrying a 30-second Gemini-grounded request burns budget 3×
// for zero UX gain — see `/api/chat` triage in handover doc.
const PER_PATH_TIMEOUTS = {
    "/chat": [90_000],   // covers Tokyo-class cold-pod outliers
};

function timeoutsFor(url) {
    // The web caller passes a full URL (`${API}/chat`, e.g.
    // `https://piqpath.com/api/chat`), the mobile caller passes a bare
    // path (`/chat`). Normalise to a path suffix match: a key like
    // `/chat` matches both shapes because the leading slash enforces a
    // segment boundary (e.g. `/somechat` will NOT match `/chat`).
    const noQuery = (url || "").split("?")[0].replace(/\/$/, "");
    for (const key of Object.keys(PER_PATH_TIMEOUTS)) {
        if (noQuery === key || noQuery.endsWith(key)) {
            return PER_PATH_TIMEOUTS[key];
        }
    }
    return DEFAULT_TIMEOUTS_MS;
}

function isRetryable(err) {
    if (!err) return false;
    if (err.code === "ECONNABORTED" || err.code === "ERR_NETWORK") return true;
    if (!err.response) return true;
    const s = err.response.status;
    return s >= 500 && s < 600;
}

function classifyFinalError(err) {
    if (!err) return new ChatRequestError("unknown", "unknown error");
    if (err.code === "ECONNABORTED") return new ChatRequestError("timeout", "request timed out");
    if (!err.response) return new ChatRequestError("network", err.message || "network error");
    const s = err.response.status;
    const body = err.response.data;
    const code = (body && (body.error || body.code)) || "";
    if (s === 429 || code === "rate_limited") return new ChatRequestError("rate_limited", "rate limited", s);
    if (s >= 500 || code === "server_error") return new ChatRequestError("server_error", "server error", s);
    return new ChatRequestError("unknown", `http ${s}`, s);
}

export async function postWithRetry(url, body, config = {}) {
    const timeouts = timeoutsFor(url);
    let lastErr = null;
    for (let attempt = 0; attempt < timeouts.length; attempt++) {
        try {
            return await axios.post(url, body, {
                ...config,
                timeout: timeouts[attempt],
            });
        } catch (err) {
            lastErr = err;
            if (!isRetryable(err)) break;
            if (attempt < timeouts.length - 1) {
                const wait = RETRY_BACKOFFS_MS[attempt];
                if (typeof console !== "undefined") {
                    console.warn(
                        `[api] retry ${attempt + 1}/${timeouts.length - 1} for ${url} in ${wait}ms`,
                        err?.message || err?.code,
                    );
                }
                await new Promise((r) => setTimeout(r, wait));
            }
        }
    }
    throw classifyFinalError(lastErr);
}
